import axios from 'axios';
import { getCsrfHeaders } from '@/utils/csrf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true, // Always send cookies
});

// Add CSRF token and Authorization header to requests
api.interceptors.request.use((config) => {
  // Add CSRF headers for state-changing requests
  if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
    const csrfHeaders = getCsrfHeaders();
    config.headers = { ...config.headers, ...csrfHeaders };
  }
  
  // Add Authorization header from localStorage as fallback for mobile browsers
  // (Safari ITP blocks third-party cookies)
  const token = localStorage.getItem('access_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  return config;
});

/**
 * Authenticated fetch wrapper for mobile compatibility
 * Use this for direct fetch calls that need authentication
 * Automatically adds Authorization header for mobile browsers that block cookies
 */
export const authenticatedFetch = async (url, options = {}) => {
  const token = localStorage.getItem('access_token');
  const csrfHeaders = getCsrfHeaders();
  
  const headers = {
    ...(options.headers || {}),
  };
  
  // Add Authorization header for mobile fallback
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add CSRF headers for state-changing requests
  if (options.method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method.toUpperCase())) {
    Object.assign(headers, csrfHeaders);
  }
  
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  });
};

// Handle auth errors - minimal logging for security
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Only log non-sensitive info in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        url: error.config?.url,
        status: error.response?.status
      });
    }

    if (error.response?.status === 401) {
      // Don't redirect if we're on the login page or already redirecting
      const currentPath = window.location.pathname;
      const publicPages = ['/login', '/register', '/forgot-password', '/newsletter/upgrade', '/newsletter/landing', '/newsletter/unsubscribe', '/newsletter/checkout'];
      if (publicPages.some(page => currentPath.startsWith(page))) {
        return Promise.reject(error);
      }
      
      // Check if the request was for a non-critical endpoint (affiliate, organization checks)
      const url = error.config?.url || '';
      const nonCriticalEndpoints = ['/affiliates/', '/organizations/my-memberships', '/subscriptions/', '/user/newsletter/preferences'];
      const isNonCritical = nonCriticalEndpoints.some(endpoint => url.includes(endpoint));
      
      if (isNonCritical) {
        // Don't redirect for non-critical 401s - just reject
        return Promise.reject(error);
      }
      
      // Clear user data and token for critical auth failures
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  getCsrfToken: () => api.get('/auth/csrf-token'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  validateResetToken: (token) => api.get(`/auth/validate-reset-token/${token}`),
};

export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
};

export const resumeAPI = {
  list: () => api.get('/resumes'),
  getAll: () => api.get('/resumes'), // Alias for list for backward compatibility
  create: (data) => api.post('/resumes', data),
  get: (id) => api.get(`/resumes/${id}`),
  update: (id, data) => api.put(`/resumes/${id}`, data),
  delete: (id) => api.delete(`/resumes/${id}`),
  upload: (file, mode = 'modernize') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mode', mode);
    return api.post('/resumes/upload', formData);
  },
  // Preview parsing without saving - allows user to review and correct
  previewParse: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/resumes/preview-parse', formData);
  },
  // Re-parse existing resume with AI
  reparseWithAI: (resumeId) => api.post(`/resumes/${resumeId}/reparse-ai`),
  // Update specific sections (for manual correction)
  updateSections: (resumeId, sections) => api.put(`/resumes/${resumeId}/sections`, sections),
  // Regenerate page renders for exact replica
  regenerateRenders: (resumeId) => api.post(`/resumes/${resumeId}/regenerate-renders`),
  // Regenerate editable HTML for true document editing
  regenerateEditableHtml: (resumeId) => api.post(`/resumes/${resumeId}/regenerate-editable-html`),
  generateSection: (data) => api.post('/resumes/generate-section', data),
  tailor: (data) => api.post('/resumes/tailor', data),
  getTemplates: () => api.get('/templates/browse').then(res => ({ data: res.data.templates })),
  applyTemplate: (resumeId, templateId) => api.post(`/resumes/${resumeId}/apply-template`, { template_id: templateId }),
  // Version History APIs
  getVersions: (resumeId) => api.get(`/resumes/${resumeId}/versions`),
  createVersion: (resumeId, data = {}) => api.post(`/resumes/${resumeId}/versions`, data),
  restoreVersion: (resumeId, versionId) => api.post(`/resumes/${resumeId}/versions/${versionId}/restore`),
  deleteVersion: (resumeId, versionId) => api.delete(`/resumes/${resumeId}/versions/${versionId}`),
};

export const coverLetterAPI = {
  list: () => api.get('/cover-letters'),
  getAll: () => api.get('/cover-letters'), // Alias for list for backward compatibility
  get: (id) => api.get(`/cover-letters/${id}`),
  generate: (data) => api.post('/cover-letters/generate', data),
  save: (data) => {
    const formData = new FormData();
    formData.append('job_title', data.job_title);
    formData.append('company', data.company);
    formData.append('content', data.content);
    if (data.resume_id) formData.append('resume_id', data.resume_id);
    return api.post('/cover-letters/save', formData);
  },
  update: (id, content) => {
    const formData = new FormData();
    formData.append('content', content);
    return api.put(`/cover-letters/${id}`, formData);
  },
  delete: (id) => api.delete(`/cover-letters/${id}`),
};

export const jobAPI = {
  search: (params) => api.get('/jobs/search', { params }),
  save: (data) => api.post('/jobs/save', data),
  getSaved: () => api.get('/jobs/saved'),
  updateStatus: (id, data) => api.put(`/jobs/saved/${id}/status`, data),
  delete: (id) => api.delete(`/jobs/saved/${id}`),
  createAlert: (data) => api.post('/jobs/alerts', data),
  getAlerts: () => api.get('/jobs/alerts'),
  deleteAlert: (id) => api.delete(`/jobs/alerts/${id}`),
};

export const personalTemplateAPI = {
  create: (data) => api.post('/personal-templates', data),
  list: () => api.get('/personal-templates'),
  get: (id) => api.get(`/personal-templates/${id}`),
  delete: (id) => api.delete(`/personal-templates/${id}`),
};

export const visualResumeAPI = {
  create: (data) => api.post('/visual-resumes', data),
  list: () => api.get('/visual-resumes'),
  getAll: () => api.get('/visual-resumes'), // Alias for list for backward compatibility
  get: (id) => api.get(`/visual-resumes/${id}`),
  update: (id, data) => api.put(`/visual-resumes/${id}`, data),
  delete: (id) => api.delete(`/visual-resumes/${id}`),
};

export const interviewPrepAPI = {
  generate: (data) => api.post('/interview-prep/generate', data),
  list: () => api.get('/interview-prep'),
  delete: (id) => api.delete(`/interview-prep/${id}`),
};

export const salaryInsightsAPI = {
  get: (data) => api.post('/salary-insights', data),
};

export const salaryReportsAPI = {
  create: (data) => api.post('/salary-reports', data),
  list: () => api.get('/salary-reports'),
  get: (id) => api.get(`/salary-reports/${id}`),
  update: (id, data) => api.put(`/salary-reports/${id}`, data),
  delete: (id) => api.delete(`/salary-reports/${id}`),
  emailReport: (id) => api.post(`/salary-reports/${id}/email`),
};

export const interviewPrepReportsAPI = {
  create: (data) => {
    const formData = new FormData();
    formData.append('job_title', data.job_title);
    formData.append('company', data.company || '');
    formData.append('job_description', data.job_description || '');
    formData.append('questions_and_answers', JSON.stringify(data.questions_and_answers));
    return api.post('/interview-prep-reports', formData);
  },
  list: () => api.get('/interview-prep-reports'),
  get: (id) => api.get(`/interview-prep-reports/${id}`),
  update: (id, data) => {
    const formData = new FormData();
    formData.append('job_title', data.job_title);
    formData.append('company', data.company || '');
    formData.append('job_description', data.job_description || '');
    formData.append('questions_and_answers', JSON.stringify(data.questions_and_answers));
    return api.put(`/interview-prep-reports/${id}`, formData);
  },
  delete: (id) => api.delete(`/interview-prep-reports/${id}`),
  emailReport: (id) => api.post(`/interview-prep-reports/${id}/email`),
};

export const jobApplicationAPI = {
  create: (data) => api.post('/job-applications', data),
  list: () => api.get('/job-applications'),
  get: (id) => api.get(`/job-applications/${id}`),
  update: (id, data) => api.put(`/job-applications/${id}`, data),
  delete: (id) => api.delete(`/job-applications/${id}`),
};

export const careerResourcesAPI = {
  list: (category) => api.get('/career-resources', { params: category ? { category } : {} }),
  get: (id) => api.get(`/career-resources/${id}`),
};

export const adminAPI = {
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // User Management
  getAllUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  updateUserRoles: (userId, roles) => api.put(`/admin/users/${userId}/roles`, { roles }),
  updateUserStatus: (userId, data) => api.put(`/admin/users/${userId}/status`, data),
  
  // Coach Management
  getCoaches: (params) => api.get('/admin/coaches', { params }),
  updateCoachStatus: (coachId, data) => api.put(`/admin/coaches/${coachId}/status`, data),
  
  // Payment Management
  getPayments: (params) => api.get('/admin/payments', { params }),
  issueRefund: (paymentId, data) => api.post(`/admin/payments/${paymentId}/refund`, data),
  
  // Audit Logs
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  
  // Legacy endpoints (keep for backward compatibility)
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/roles`, { roles: [role] }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  assignCoachRole: (userId, options = {}) => api.put(`/admin/users/${userId}/coach-role`, { make_coach: true, coach_type: options.coach_type || 'careersupport' }),
  revokeCoachRole: (userId) => api.put(`/admin/users/${userId}/coach-role`, { make_coach: false }),
  getCoachMembers: (coachId) => api.get(`/admin/coaches/${coachId}/members`),
};

export const assessmentAPI = {
  getQuestions: (type) => api.get(`/assessments/${type}/questions`),
  submit: (data) => api.post('/assessments/submit', data),
  getResults: () => api.get('/assessments/results'),
  getResult: (id) => api.get(`/assessments/results/${id}`),
  emailResults: (id) => api.post(`/assessments/results/${id}/email`),
  deleteResult: (id) => api.delete(`/assessments/results/${id}`),
};

export const careerDataAPI = {
  getInterestArea: (area) => api.get(`/career-data/${area}`),
  getWorkStyleData: (profileName) => api.get(`/work-style-data/${profileName}`),
};

export const savedCareersAPI = {
  save: (data) => api.post('/saved-careers', data),
  list: () => api.get('/saved-careers'),
  getAll: () => api.get('/saved-careers'), // Alias for list for backward compatibility
  delete: (id) => api.delete(`/saved-careers/${id}`),
};

export const atsAPI = {
  runCheck: (data) => api.post('/ats-check', data),
  getReports: () => api.get('/ats-reports'),
  getReport: (id) => api.get(`/ats-reports/${id}`),
  getReportsByResume: (resumeId) => api.get(`/ats-reports/resume/${resumeId}`),
  deleteReport: (id) => api.delete(`/ats-reports/${id}`),
  emailReport: (id) => api.post(`/ats-reports/${id}/email`),
};

export const careerPlannerAPI = {
  generate: (data) => api.post('/career-planner/generate', data),
  getHistory: () => api.get('/career-planner/history'),
  getUserPlans: () => api.get('/career-planner/history'), // Alias for getHistory for backward compatibility
  getPlan: (planId) => api.get(`/career-planner/${planId}`),
  deletePlan: (planId) => api.delete(`/career-planner/${planId}`),
  emailPlan: (planId) => api.post(`/career-planner/${planId}/email`),
};

export const aiAssistantAPI = {
  chat: (data) => api.post('/ai-assistant/chat', data),
  getSession: (sessionId) => api.get(`/ai-assistant/session/${sessionId}`),
  clearSession: (sessionId) => api.delete(`/ai-assistant/session/${sessionId}`),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  changePassword: (data) => api.put('/settings/account/password', data),
  changeEmail: (data) => api.put('/settings/account/email', data),
  changeUsername: (username) => api.put('/settings/account/username', { username }),
  resetDashboard: () => api.post('/settings/reset-dashboard'),
  saveDashboardSectionOrder: (sectionOrder) => api.post('/settings/dashboard-section-order', sectionOrder),
  getLoginHistory: () => api.get('/settings/login-history'),
  deleteAccount: (password) => api.delete('/settings/account', { data: { password } }),
};

export const coachAPI = {
  // For coaches
  getMembers: () => api.get('/coach/members'),
  getMemberData: (memberId) => api.get(`/coach/members/${memberId}/data`),
  
  // For members
  getAvailableCoaches: () => api.get('/coaches/available'),
  requestConnection: (coachId) => api.post('/coach-connections/request', { coach_id: coachId }),
  grantAccess: (connectionId) => api.put(`/coach-connections/${connectionId}/grant`),
  revokeAccess: (connectionId) => api.put(`/coach-connections/${connectionId}/revoke`),
  getMyCoaches: () => api.get('/my-coaches'),
  
  // My Connections (networking without full coach access) - works for BOTH users and coaches
  getMyConnections: () => api.get('/coaching/my-connections'),
  sendConnectionRequest: (targetId) => api.post('/coaching/send-connection-request', { target_id: targetId }),
  getPendingRequests: () => api.get('/coaching/my-pending-requests'),
  respondToRequest: (connectionId, action) => api.post(`/coaching/connection-request/${connectionId}/respond`, { action }),
  revokeConnection: (connectionId) => api.delete(`/coaching/connection/${connectionId}/revoke`),
};

export const coachingAPI = {
  // Profile
  getCoachProfile: (userId) => api.get(`/coaching/coach-profile/${userId}`),
  updateCoachProfile: (data) => api.put('/coaching/coach-profile', data),
  searchCoaches: (params) => api.get('/coaching/coaches/search', { params }),
  
  // Messages
  sendMessage: (data) => api.post('/coaching/messages', data),
  getMessages: (connectionId) => api.get(`/coaching/messages/${connectionId}`),
  getUnreadCount: (connectionId) => api.get(`/coaching/messages/${connectionId}/unread-count`),
  
  // Sessions
  createSession: (data) => api.post('/coaching/sessions', data),
  getSessions: (params) => api.get('/coaching/sessions', { params }),
  getMySessions: () => api.get('/coaching/my-sessions'),
  
  // Coach client management
  sendClientRequest: (clientId) => api.post('/coaching/client-request/send', { client_id: clientId }),
  sendConnectionRequest: (memberId) => api.post('/coaching/send-connection-request', { member_id: memberId }),
  getMyClients: (params) => api.get('/coaching/my-clients', { params }),
  getClientRelationships: (params) => api.get('/coaching/client-request/list', { params }),
  getMyConnections: () => api.get('/coach-connections/my-connections'),
  revokeConnection: (connectionId) => api.delete(`/coach-connections/${connectionId}`),
  respondToClientRequest: (relationshipId, action) => api.put(`/coaching/client-request/${relationshipId}/respond`, { action }),
  
  // Send forms/contracts to members
  sendIntakeToMember: (memberId, intakeFormId, message) => api.post('/coaching/send-intake-to-member', { member_id: memberId, intake_form_id: intakeFormId, message }),
  sendContractToMember: (memberId, contractId, message) => api.post('/coaching/send-contract-to-member', { member_id: memberId, contract_id: contractId, message }),
  
  // Coach manual forms/contracts
  sendIntakeForm: (data) => api.post('/coaching/send-intake-form', data),
  sendContract: (data) => api.post('/coaching/send-contract', data),
  
  // Coach tracking
  getCoachIntakeForms: (status) => api.get('/coaching/coach/intake-forms-sent', { params: { status } }),
  getCoachContracts: (status) => api.get('/coaching/coach/contracts-sent', { params: { status } }),
  updateSession: (sessionId, data) => api.put(`/coaching/sessions/${sessionId}`, data),
  
  // Suggestions
  createSuggestion: (data) => api.post('/coaching/suggestions', data),
  getSuggestions: (params) => api.get('/coaching/suggestions', { params }),
  respondToSuggestion: (suggestionId, action) => api.put(`/coaching/suggestions/${suggestionId}/respond`, { action }),
  
  // Goals
  createGoal: (data) => api.post('/coaching/goals', data),
  getGoals: (params) => api.get('/coaching/goals', { params }),
  updateGoal: (goalId, data) => api.put(`/coaching/goals/${goalId}`, data),
  
  // Reviews
  createReview: (data) => api.post('/coaching/reviews', data),
  getCoachReviews: (coachId) => api.get(`/coaching/reviews/${coachId}`),
  respondToReview: (reviewId, response) => api.put(`/coaching/reviews/${reviewId}/respond`, { response }),
  
  // Notifications
  getNotifications: (unreadOnly = false) => api.get('/coaching/notifications', { params: { unread_only: unreadOnly } }),
  markNotificationRead: (notificationId) => api.put(`/coaching/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => api.put('/coaching/notifications/mark-all-read'),
  getUnreadNotificationCount: () => api.get('/coaching/notifications/unread-count'),
  deleteNotification: (notificationId) => api.delete(`/coaching/notifications/${notificationId}`),
  
  // Payments
  createPaymentIntent: (data) => api.post('/coaching/create-payment-intent', data),
  getEarnings: () => api.get('/coaching/earnings'),
  
  // Session management
  deleteSession: (sessionId) => api.delete(`/coaching/sessions/${sessionId}`),
  
  // Intake Forms
  createIntakeForm: (data) => api.post('/coaching/intake-forms', data),
  getMyIntakeForms: () => api.get('/coaching/intake-forms'),
  getIntakeForm: (formId) => api.get(`/coaching/intake-forms/${formId}`),
  updateIntakeForm: (formId, data) => api.put(`/coaching/intake-forms/${formId}`, data),
  deleteIntakeForm: (formId) => api.delete(`/coaching/intake-forms/${formId}`),
  submitIntakeForm: (formId, data) => api.post(`/coaching/intake-forms/${formId}/submit`, data),
  getFormResponses: (formId) => api.get(`/coaching/intake-forms/${formId}/responses`),
  getMySubmissions: () => api.get('/coaching/intake-forms/responses/my-submissions'),
  getIntakeResponse: (responseId) => api.get(`/coaching/intake-forms/responses/${responseId}`),
  
  // Contracts
  createContract: (data) => api.post('/coaching/contracts', data),
  getMyContracts: () => api.get('/coaching/contracts'),
  getContract: (contractId) => api.get(`/coaching/contracts/${contractId}`),
  updateContract: (contractId, data) => api.put(`/coaching/contracts/${contractId}`, data),
  deleteContract: (contractId) => api.delete(`/coaching/contracts/${contractId}`),
  signContract: (contractId, data) => api.post(`/coaching/contracts/${contractId}/sign`, data),
  getContractSignatures: (contractId) => api.get(`/coaching/contracts/${contractId}/signatures`),
  getMySignatures: () => api.get('/coaching/contracts/signatures/my-signatures'),
  getSignature: (signatureId) => api.get(`/coaching/contracts/signatures/${signatureId}`),
  checkIfSigned: (contractId) => api.get(`/coaching/contracts/check-signature/${contractId}`),
  
  // Availability
  getMyAvailability: () => api.get('/coaching/availability'),
  updateAvailability: (data) => api.put('/coaching/availability', data),
  addBlackoutDate: (data) => api.post('/coaching/availability/blackout-dates', data),
  removeBlackoutDate: (blackoutId) => api.delete(`/coaching/availability/blackout-dates/${blackoutId}`),
  getCoachAvailabilityPublic: (coachId) => api.get(`/coaching/availability/${coachId}/public`),
  
  // Contract Review (for members)
  getContractDetails: (contractId) => api.get(`/coaching/contracts/${contractId}/details`),
  getContractRequirement: (contractId) => api.get(`/coaching/contracts/${contractId}/requirement`)
};

export const journeyAPI = {
  startJourney: (data) => api.post('/journey/start', data),
  getCurrentJourney: (coachId) => api.get(`/journey/current`, { params: { coach_id: coachId } }),
  updateJourneyStep: (data) => api.put('/journey/update-step', data),
  getIncompleteJourneys: () => api.get('/journey/incomplete-journeys'),
  sendReminder: (journeyId) => api.post(`/journey/send-reminder/${journeyId}`)
};

export const searchAPI = {
  searchUsers: (query) => api.get('/coaching/search-members', { params: { q: query } }),
};

// Progress Tracking API - for event-driven milestone tracking
export const progressAPI = {
  // Log a progress event
  trackEvent: async (eventType, toolKey, metadata = {}, organizationId = null, cohortId = null) => {
    try {
      await api.post('/progress/events', {
        event_type: eventType,
        tool_key: toolKey,
        organization_id: organizationId,
        cohort_id: cohortId,
        metadata
      });
    } catch (error) {
      // Silently fail - don't interrupt user flow for tracking failures
      console.debug('Progress tracking event failed:', error);
    }
  },
  
  // Get user's progress events
  getEvents: (params = {}) => api.get('/progress/events', { params }),
  
  // Admin: Get all progress events with filters
  getAdminEvents: (params = {}) => api.get('/progress/admin/events', { params }),
  
  // Get engagement score for a user
  getEngagementScore: (userId) => api.get(`/progress/engagement-score/${userId}`),
  
  // Track tool usage (for Recently Used feature)
  trackToolUsage: (toolKey, toolName) => api.post('/tools/track-usage', { tool_key: toolKey, tool_name: toolName }),
  
  // Get recently used tools
  getRecentlyUsed: (limit = 5) => api.get('/tools/recently-used', { params: { limit } }),
  
  // Get tool badge status
  getBadgeStatus: () => api.get('/tools/badge-status'),
  
  // Get recommended next action
  getRecommendedAction: () => api.get('/dashboard/recommended-action')
};

// Cohort Management API
export const cohortAPI = {
  // List all cohorts
  list: (params = {}) => api.get('/admin/cohorts', { params }),
  
  // Create a new cohort
  create: (data) => api.post('/admin/cohorts', data),
  
  // Update a cohort
  update: (cohortId, data) => api.put(`/admin/cohorts/${cohortId}`, data),
  
  // Delete a cohort
  delete: (cohortId) => api.delete(`/admin/cohorts/${cohortId}`),
  
  // Get cohort members
  getMembers: (cohortId) => api.get(`/admin/cohorts/${cohortId}/members`),
  
  // Add a single member
  addMember: (cohortId, data) => api.post(`/admin/cohorts/${cohortId}/members`, data),
  
  // Bulk add members (CSV import)
  bulkAddMembers: (cohortId, members) => api.post(`/admin/cohorts/${cohortId}/members/bulk`, { members }),
  
  // Remove a member
  removeMember: (cohortId, memberId) => api.delete(`/admin/cohorts/${cohortId}/members/${memberId}`),
  
  // Update tool assignments
  updateToolAssignments: (cohortId, toolAssignments) => 
    api.put(`/admin/cohorts/${cohortId}/tool-assignments`, { toolAssignments }),
  
  // Get cohort analytics
  getAnalytics: (cohortId) => api.get(`/admin/cohorts/${cohortId}/analytics`)
};

// LMS/Learning API
export const learningAPI = {
  // Course management
  listCourses: (params = {}) => api.get('/learning/courses', { params }),
  getCourse: (courseId) => api.get(`/learning/courses/${courseId}`),
  previewCourse: (courseId) => api.get(`/learning/courses/${courseId}/preview`),
  createCourse: (data) => api.post('/learning/courses', data),
  updateCourse: (courseId, data) => api.put(`/learning/courses/${courseId}`, data),
  deleteCourse: (courseId) => api.delete(`/learning/courses/${courseId}`),
  
  // Module management
  createModule: (data) => api.post('/learning/modules', data),
  updateModule: (moduleId, data) => api.put(`/learning/modules/${moduleId}`, data),
  deleteModule: (moduleId) => api.delete(`/learning/modules/${moduleId}`),
  
  // Lesson management
  createLesson: (data) => api.post('/learning/lessons', data),
  updateLesson: (lessonId, data) => api.put(`/learning/lessons/${lessonId}`, data),
  deleteLesson: (lessonId) => api.delete(`/learning/lessons/${lessonId}`),
  
  // Enrollment
  enroll: (courseId) => api.post(`/learning/courses/${courseId}/enroll`),
  getMyEnrollments: () => api.get('/learning/my-enrollments'),
  
  // Course analytics
  getCourseAnalytics: (courseId) => api.get(`/learning/analytics/courses/${courseId}`),
  getDashboardStats: () => api.get('/learning/analytics/dashboard')
};

// GED/HiSET/ABE Program API
export const gedAPI = {
  // Check application status (returns user's application with status field)
  getApplicationStatus: () => api.get('/ged/application'),
  // Get user's GED progress summary
  getProgressSummary: () => api.get('/ged/user-progress-summary'),
};

export default api;
